import { useState } from 'react';
import { useI18n } from '../i18n';

interface SaveInstallationModalProps {
  onSave: (name: string, description: string) => void;
  onClose: () => void;
  defaultName?: string;
  defaultDescription?: string;
  isUpdate?: boolean;
}

export function SaveInstallationModal({
  onSave,
  onClose,
  defaultName = '',
  defaultDescription = '',
  isUpdate = false,
}: SaveInstallationModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState(defaultDescription);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(name.trim(), description.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">
            {isUpdate ? t('arch_save_update_title') : t('arch_save_title')}
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            {isUpdate ? t('arch_save_update_sub') : t('arch_save_sub')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
              {t('arch_save_name_label')}
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('arch_save_name_ph')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              required
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-1">
              {t('arch_save_desc_label')}
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('arch_save_desc_ph')}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg border border-slate-300 transition-colors"
            >
              {t('arch_cancel')}
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow"
            >
              {isUpdate ? t('arch_update') : t('arch_save_btn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
